// PPT 테마 payload — 바닐라 dcf.js 의 buildPptThemePayload(custom 분기 포함)와
// renderLogoPreview 의 표지 배치 계산을 순수 함수로 이식.
// FileReader/Image/canvas 등 DOM 의존 로직은 ReportTab 쪽에 둔다.

/** 바닐라 PPT_PRESETS — key/색상은 백엔드 dcf_ppt.py 프리셋과 1:1 */
export const PPT_PRESETS = [
  { key: "navy_classic", navy: "#1F3364", blueMid: "#4A82D4", labelKey: "ppt.navy_classic" },
  { key: "corporate_blue", navy: "#0B3D91", blueMid: "#42A5F5", labelKey: "ppt.corporate_blue" },
  { key: "forest_green", navy: "#1B4332", blueMid: "#52B788", labelKey: "ppt.forest_green" },
  { key: "amber_orange", navy: "#9A3412", blueMid: "#FB923C", labelKey: "ppt.amber_orange" },
  { key: "monochrome", navy: "#000000", blueMid: "#8C8C8C", labelKey: "ppt.monochrome" },
] as const;

/** 바닐라 dcf.html #ppt-font 옵션과 동일 */
export const PPT_FONTS = ["Malgun Gothic", "Pretendard", "Noto Sans KR", "Arial"] as const;

export interface PptThemeState {
  preset: string;
  /** '색상·폰트도 직접 커스터마이징' 체크 여부 */
  custom: boolean;
  navy?: string;
  blueMid?: string;
  font?: string;
  logoText?: string;
  logoImageBase64?: string | null;
}

/** 백엔드 /agent/dcf/ppt 가 받는 ppt_theme 형태 */
export interface PptThemePayload {
  preset: string;
  navy?: string;
  blue_mid?: string;
  font?: string;
  logo_text?: string;
  logo_image_base64?: string;
}

/** 바닐라 buildPptThemePayload — 기본 프리셋 + 로고/커스텀 없음이면 null(테마 미전송).
 *  로고 문구/이미지는 custom 체크와 무관하게 항상 적용. */
export function buildPptTheme(s: PptThemeState): PptThemePayload | null {
  const logoText = (s.logoText || "").trim();
  const hasLogo = !!(logoText || s.logoImageBase64);
  if (!s.custom) {
    if (s.preset === "navy_classic" && !hasLogo) return null;
    const theme: PptThemePayload = { preset: s.preset };
    if (logoText) theme.logo_text = logoText;
    if (s.logoImageBase64) theme.logo_image_base64 = s.logoImageBase64;
    return theme;
  }
  const theme: PptThemePayload = { preset: s.preset };
  if (s.navy) theme.navy = s.navy;
  if (s.blueMid) theme.blue_mid = s.blueMid;
  if (s.font) theme.font = s.font;
  if (logoText) theme.logo_text = logoText;
  if (s.logoImageBase64) theme.logo_image_base64 = s.logoImageBase64;
  return theme;
}

// 표지 슬라이드 로고 배치 규칙과 동일한 값(dcf_ppt.py _slide_cover 의
// height_in/max_width_in) — 실제 PPT 배치를 그대로 미리보기하기 위한 계산.
export const LOGO_COVER_HEIGHT_IN = 0.34;
export const LOGO_COVER_MAXWIDTH_IN = 2.4;
export const LOGO_PREVIEW_PX_PER_IN = 60;

export interface LogoPlacement {
  /** 표지 배치 크기 (inch) */
  w: number;
  h: number;
  /** 가로가 길어 너비 기준으로 잘렸는지 */
  capped: boolean;
}

/** 바닐라 renderLogoPreview 의 배치 계산부 — 원본 픽셀 크기 → 표지 inch 크기 */
export function computeLogoPlacement(natW: number, natH: number): LogoPlacement | null {
  if (!natW || !natH) return null;
  const ratio = natW / natH;
  let w = LOGO_COVER_HEIGHT_IN * ratio;
  let h = LOGO_COVER_HEIGHT_IN;
  const capped = w > LOGO_COVER_MAXWIDTH_IN;
  if (capped) {
    w = LOGO_COVER_MAXWIDTH_IN;
    h = LOGO_COVER_MAXWIDTH_IN / ratio;
  }
  return { w, h, capped };
}
